import React from "react";
import { ArrowRight, Check } from "lucide-react";

const PLATE_IMG =
    "https://customer-assets.emergentagent.com/job_haiti-flavors-boston/artifacts/xxuvfmzk_file_00000000f5f471fd9da8c76938be1577.png";

const WHY_BULLETS = [
    "Authentic Caribbean Flavor",
    "Fresh Weekly Meals",
    "Portion Controlled",
    "Ready in Minutes",
    "Made for Busy People",
];

export default function Hero({ onSeeMenu }) {
    return (
        <section
            data-testid="hero-section"
            className="hero-gradient grain relative overflow-hidden text-white"
        >
            {/* Plate image overlays — small, soft, layered into the green */}
            {/* Single plate image behind the text — green dominates, plate is a subtle backdrop */}
            <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                aria-hidden
            >
                <img
                    src={PLATE_IMG}
                    alt=""
                    data-testid="hero-plate-backdrop"
                    className="w-[70%] sm:w-[55%] md:w-[45%] max-w-[540px] mt-24 opacity-25 mix-blend-luminosity select-none animate-float-slower"
                />
            </div>

            {/* Faint accent emojis kept for organic feel */}
            <span
                aria-hidden
                className="absolute top-[22%] right-[26%] text-5xl opacity-20 animate-float-slower select-none"
            >
                🍍
            </span>
            <span
                aria-hidden
                className="absolute bottom-[24%] right-[18%] text-4xl opacity-15 animate-float-slow select-none"
            >
                🌶️
            </span>

            <div className="max-w-5xl mx-auto px-6 sm:px-8 py-20 md:py-28 flex flex-col items-center text-center relative z-10">
                <div
                    data-testid="hero-pill-badge"
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold/15 backdrop-blur-md border border-gold/30 text-gold text-xs sm:text-sm tracking-wider font-medium animate-fade-up"
                >
                    <span>🇭🇹</span>
                    <span>Boston, MA · Haitian-Inspired</span>
                </div>

                <h1
                    className="mt-8 font-serif font-black leading-[0.95] text-white animate-fade-up"
                    style={{ animationDelay: "160ms" }}
                >
                    <span className="block text-5xl sm:text-6xl md:text-7xl">
                        Caribbean
                    </span>
                    <span className="block text-5xl sm:text-6xl md:text-7xl text-gold italic">
                        Meal Prep
                    </span>
                </h1>

                <p
                    className="mt-5 text-[10px] sm:text-xs tracking-[0.4em] font-bold text-mint/80 animate-fade-up"
                    style={{ animationDelay: "220ms" }}
                >
                    BOSTON · MASSACHUSETTS
                </p>

                {/* New headline */}
                <h2
                    data-testid="hero-headline"
                    className="mt-8 max-w-2xl font-serif font-bold text-2xl sm:text-3xl md:text-4xl leading-tight text-white animate-fade-up"
                    style={{ animationDelay: "280ms" }}
                >
                    Caribbean Comfort Food{" "}
                    <span className="text-gold italic">Without the Guilt.</span>
                </h2>

                <p
                    data-testid="hero-subcopy"
                    className="mt-4 max-w-xl text-[15px] sm:text-base leading-relaxed text-white/85 animate-fade-up"
                    style={{ animationDelay: "340ms" }}
                >
                    Healthy Caribbean meal prep made fresh weekly for busy
                    lifestyles.
                </p>

                {/* Why card */}
                <div
                    data-testid="hero-why-card"
                    className="mt-10 w-full max-w-md rounded-2xl bg-white/8 backdrop-blur-md border border-white/15 p-6 sm:p-7 text-left animate-fade-up"
                    style={{ animationDelay: "400ms" }}
                >
                    <p className="text-[10px] sm:text-xs tracking-[0.3em] font-bold text-gold mb-4">
                        WHY CARIBBEAN MEAL PREP?
                    </p>
                    <ul className="space-y-3">
                        {WHY_BULLETS.map((item, idx) => (
                            <li
                                key={item}
                                data-testid={`hero-why-bullet-${idx}`}
                                className="flex items-start gap-3 text-[15px] sm:text-base text-white/95"
                            >
                                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-mint/20 ring-1 ring-mint/40">
                                    <Check
                                        size={12}
                                        strokeWidth={3}
                                        className="text-mint"
                                    />
                                </span>
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <button
                    data-testid="hero-see-menu-btn"
                    onClick={onSeeMenu}
                    className="mt-10 group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold hover:bg-goldDark text-navy font-bold text-base transition-all duration-200 active:scale-95 shadow-[0_10px_30px_rgba(244,164,53,0.45)] animate-fade-up"
                    style={{ animationDelay: "460ms" }}
                >
                    Order This Week's Meals
                    <ArrowRight
                        size={18}
                        className="group-hover:translate-x-0.5 transition-transform"
                    />
                </button>
            </div>
        </section>
    );
}
